import React, {useState, useEffect, useCallback, Dispatch, useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, HStack, VStack, Stack, Text, useMultiStyleConfig, StackProps, TextProps, BoxProps} from '@chakra-ui/react'
import {Button, ColorCircle, Icon} from '_uiComponents'
import {ProductType} from '_types'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Navigation} from 'swiper/modules'
import type SwiperType from 'swiper'
import {useViewport} from '_customHooks'
import SizeSelector from './partials/size-selector'

interface ProductTileOverlayProps {
    product: ProductType
    selectedSize?: string | null
    sizeErrorVisible: boolean
    setSizeErrorVisible: Dispatch<React.SetStateAction<boolean>>
    setSelectedSize: Dispatch<React.SetStateAction<string | null>>
    setSelectedVariant: Dispatch<React.SetStateAction<string | null>>
    addProductToCart: () => void
}

/**
 * @name ProductTileOverlay
 *
 * @component
 *
 * @description
 * Overlay rendered on top of product tile, enabled when user clicks on '+' icon button.
 *
 * @param {ProductTileOverlayProps} props - The properties for the SizeSelector component.
 * @param {ProductType} props.product - The product for which sizes are being selected.
 * @param {string | null} [props.selectedSize] - Optional selected size. Can be null if no size is selected.
 * @param {Dispatch<React.SetStateAction<string | null>>} props.setSelectedSize - Function to update the selected size.
 * @param {Dispatch<React.SetStateAction<string | null>>} props.setSelectedVariant - Function to update the selected variant.
 *
 * @returns {JSX.Element} The rendered `ProductTileOverlay` component.
 */
const ProductTileOverlay = ({
    product,
    selectedSize,
    sizeErrorVisible,
    setSizeErrorVisible,
    setSelectedSize,
    setSelectedVariant,
    addProductToCart,
}: ProductTileOverlayProps) => {
    const {isMobile} = useViewport()
    const [isColorSwatchActive, setIsColorSwatchActive] = useState<boolean>(!isMobile ? true : false)
    const [isSizeListActive, setIsSizeListActive] = useState<boolean>(true)
    const activeImageIndex = useRef<number | null>(null)
    const colorsRowWrapperRef = useRef<HTMLDivElement>(null)
    const swiperRef = useRef<{swiper: SwiperType}>(null)
    const arrowsSize = 32 * 2
    // Figma "Swatch colore PLP" column (75px) + 5px inter-item gap = footprint used to compute inline capacity.
    const maxColorSwatchWidth = 80

    // Per Figma: up to 5 colours are shown inline; the carousel activates when there are more.
    const maxVisibleColors = 5
    const minSlidesToShow = 4
    const variationGroups = product?.c_variationGroupsData
    const colorsLength = variationGroups?.length || 0
    const [slidesPerView, setSlidesPerView] = useState<number | null>(null)

    const activeVariationGroup = variationGroups?.find((variation) => variation.default)
    const hasPriceRange = activeVariationGroup?.pricing?.hasPriceRange
    const selectedVariant = activeVariationGroup?.variants?.find((variant) => variant.ID === selectedSize)
    const selectedVariantPrice = selectedVariant?.pricing?.price?.formattedValue

    const colors = variationGroups?.map((variationGroup, index) => {
        const isDefault = variationGroup.default || false
        if (isDefault) {
            activeImageIndex.current = index
        }
        return {
            id: variationGroup.ID,
            name: variationGroup.color?.label,
            hexCodes: variationGroup.color?.colorHexCode ?? null,
            isDefault,
            selectable: variationGroup.selectable,
        }
    })

    const hasMultipleColors = colors && colors.length > 1

    // Slides are the selectable colours only; Swiper's initialSlide/slideTo must index into
    // THIS filtered list (not the raw variationGroups) to land on the currently shown colour.
    const selectableColors = colors?.filter((color) => color.selectable) ?? []
    const selectableColorsLength = selectableColors.length
    const currentColorIndex = Math.max(
        0,
        selectableColors.findIndex((color) => color.isDefault),
    )

    /**
     * Scroll the carousel so the current colour is visible, without breaking Swiper when the
     * colour is the last/second-to-last: Swiper can't put a trailing slide first (it stops at the
     * last page), so we clamp the target to the last valid start index and skip the scroll when the
     * colour is already inside the visible window. After moving we refresh Swiper + its navigation
     * so the prev/next arrows' disabled state stays in sync.
     */
    const scrollToCurrentColor = useCallback(
        (perView: number | null, swiperInstance?: SwiperType) => {
            const swiper = swiperInstance ?? swiperRef.current?.swiper
            if (!swiper || !perView) return

            const visibleCount = Math.max(1, Math.floor(perView))
            const maxStartIndex = Math.max(0, selectableColorsLength - visibleCount)
            const firstVisible = swiper.activeIndex ?? 0
            const lastVisible = firstVisible + visibleCount - 1

            // Already visible → don't move (avoids needless jumps and keeps arrow state as-is).
            const isAlreadyVisible = currentColorIndex >= firstVisible && currentColorIndex <= lastVisible
            const target = Math.min(currentColorIndex, maxStartIndex)

            if (!isAlreadyVisible) {
                swiper.slideTo(target, 0)
            }

            // Re-sync Swiper geometry and the navigation arrows' enabled/disabled state.
            swiper.update()
            swiper.navigation?.update()
        },
        [currentColorIndex, selectableColorsLength],
    )

    const alreadyFocused = useRef<boolean>(false)
    const autoFocus = (element: HTMLElement | null) => {
        if (element && !alreadyFocused.current) {
            element.focus()
            alreadyFocused.current = true
        }
    }

    const styles = useMultiStyleConfig('ProductTileOverlay') as Record<string, BoxProps & StackProps & TextProps>
    const {formatMessage} = useIntl()

    const availableColorLabel = formatMessage({id: 'available.color', defaultMessage: 'Available colors'})
    // Short labels for the mobile footer togglers (Figma: "Taglia" / "Colore").
    // Reuse the symmetric compiled keys filter.label.size ("Taglia") / filter.label.color ("Colore")
    // so both labels share the same casing/style and are localized without a fresh compile step.
    const sizeTabLabel = formatMessage({id: 'filter.label.size', defaultMessage: 'Size'})
    const colorTabLabel = formatMessage({id: 'filter.label.color', defaultMessage: 'Colour'})
    // Selected size label shown at the right of the Taglia toggler (mirrors the colour dot).
    const selectedSizeLabel = selectedVariant?.size?.label

    interface ColorVariant {
        id: string
        name: string
        hexCodes: string | null
        isDefault: boolean
        selectable: boolean
    }

    const renderColors = (colors: ColorVariant[], isSlider?: boolean) => {
        function sandDyTracking(color: string) {
            // Dynamic Yield change attribute Tracking
            if (typeof window !== 'undefined' && window.DY && typeof window.DY.API === 'function') {
                try {
                    window.DY.API('event', {
                        name: 'Change Attribute',
                        properties: {
                            dyType: 'change-attr-v1',
                            attributeType: 'color',
                            attributeValue: color,
                        },
                    })
                } catch (error) {
                    console.error('[DY Tracking] Errore nel tracking attribute filter:', error)
                }
            }
        }
        // Hex swatch: the backend provides `colorHexCode` on every color group
        // (see specs/color_selector_hexcode_requirements_SCAPI.md).
        const renderSwatchVisual = (color: ColorVariant) => (
            <Box {...styles.colorSwatch}>
                <ColorCircle hexCodes={color.hexCodes} selected={color.isDefault} />
            </Box>
        )

        return colors
            ?.filter((color) => color.selectable)
            ?.map((color, index) => {
                const swatch = (
                    <VStack
                        key={isSlider ? undefined : `${color.id}-${index}`}
                        {...styles.colorWrapper}
                        as="button"
                        data-element-type="color-button"
                        onClick={() => {
                            setSelectedVariant(color.id)
                            sandDyTracking(color.name)
                        }}
                        tabIndex={0}
                        ref={index === 0 ? autoFocus : undefined}
                        aria-pressed={color.isDefault}
                        aria-label={color.name}
                    >
                        {renderSwatchVisual(color)}
                        <Text {...styles.colorName}>{color.name}</Text>
                    </VStack>
                )

                return isSlider ? <SwiperSlide key={`${color.id}-${index}`}>{swatch}</SwiperSlide> : swatch
            })
    }

    // Mobile-only: colours render as a vertical scrollable list (Figma "Color slot"): a hex dot +
    // the colour label per row, with a bottom divider. The list scrolls when there are more than
    // the visible cap. Desktop keeps the swatch row / carousel (renderColors) untouched.
    const renderColorsList = (colors: ColorVariant[]) => {
        function sandDyTracking(color: string) {
            if (typeof window !== 'undefined' && window.DY && typeof window.DY.API === 'function') {
                try {
                    window.DY.API('event', {
                        name: 'Change Attribute',
                        properties: {dyType: 'change-attr-v1', attributeType: 'color', attributeValue: color},
                    })
                } catch (error) {
                    console.error('[DY Tracking] Errore nel tracking attribute filter:', error)
                }
            }
        }

        return colors
            ?.filter((color) => color.selectable)
            ?.map((color, index) => (
                <HStack
                    key={`${color.id}-${index}`}
                    as="button"
                    data-element-type="color-button"
                    {...styles.colorSlot}
                    onClick={() => {
                        setSelectedVariant(color.id)
                        sandDyTracking(color.name)
                    }}
                    tabIndex={0}
                    aria-pressed={color.isDefault}
                    aria-label={color.name}
                >
                    <Box {...styles.colorSlotSwatch} aria-hidden="true">
                        <ColorCircle hexCodes={color.hexCodes} selected={color.isDefault} />
                    </Box>
                    <Text {...styles.colorSlotLabel}>{color.name}</Text>
                </HStack>
            ))
    }

    const updateSlidesPerView = () => {
        if (colorsRowWrapperRef.current) {
            const width = colorsRowWrapperRef.current.clientWidth
            let calculated = Math.floor((width - arrowsSize) / maxColorSwatchWidth)

            // Never show more than the design cap (5) inline; beyond that the carousel takes over.
            if (calculated > maxVisibleColors) calculated = maxVisibleColors
            if (calculated > colorsLength) calculated = colorsLength
            if (calculated < minSlidesToShow) calculated = minSlidesToShow
            setSlidesPerView(calculated)
        }
    }

    useEffect(() => {
        if (isColorSwatchActive && isSizeListActive && isMobile) {
            setIsSizeListActive(false)
        }
    }, [isColorSwatchActive])

    useEffect(() => {
        if (isSizeListActive && isColorSwatchActive && isMobile) {
            setIsColorSwatchActive(false)
        }
    }, [isSizeListActive])

    useEffect(() => {
        if (selectedSize !== null && isMobile) {
            setIsSizeListActive(false)
        }
    }, [selectedSize])

    useEffect(() => {
        updateSlidesPerView()

        window.addEventListener('resize', updateSlidesPerView)
        return () => window.removeEventListener('resize', updateSlidesPerView)
    }, [colorsLength])

    // Keep the selected colour in view: scroll the carousel to it whenever it (or the layout) changes,
    // then re-sync the navigation arrows.
    useEffect(() => {
        scrollToCurrentColor(slidesPerView)
    }, [currentColorIndex, slidesPerView, scrollToCurrentColor])

    return (
        <VStack
            align="start"
            spacing={3}
            {...styles.wrapper}
            ref={colorsRowWrapperRef}
            data-testid="product-tile-overlay"
            id={`product-tile-overlay-${product.id}`}
        >
            {hasMultipleColors && !slidesPerView ? undefined : (
                <>
                    {/* Colors */}
                    {hasMultipleColors &&
                        isColorSwatchActive &&
                        (() => {
                            const isCarousel = !isMobile && slidesPerView !== null && colors.length > slidesPerView

                            return (
                                <VStack {...styles.colorsRowWrapper} data-testid="color-selector">
                                    {/* Header: title (left) + navigation arrows (right), aligned per Figma */}
                                    {!isMobile && (
                                        <HStack {...styles.colorsHeaderRow}>
                                            <Text {...styles.selectionTitle} {...styles.colorsTitle}>
                                                {availableColorLabel}
                                            </Text>
                                            {isCarousel && (
                                                <HStack {...styles.colorsNavigation}>
                                                    <Stack
                                                        as="button"
                                                        aria-label={formatMessage({id: 'aria.color.prev', defaultMessage: 'Previous colors'})}
                                                        className={`swiper-button-prev-color-selector`}
                                                        cursor="pointer"
                                                        data-testid="swiper-button-prev"
                                                        // Use `sx` (not prop spread) so the nested `&.swiper-button-disabled svg path`
                                                        // selector is emitted as CSS (spread would drop selector keys).
                                                        sx={styles.colorsNavigationButton}
                                                    >
                                                        <Icon name="IconChevronLeft" size="sm" />
                                                    </Stack>
                                                    <Stack
                                                        as="button"
                                                        aria-label={formatMessage({id: 'aria.color.next', defaultMessage: 'Next colors'})}
                                                        className={`swiper-button-next-color-selector`}
                                                        cursor="pointer"
                                                        data-testid="swiper-button-next"
                                                        sx={styles.colorsNavigationButton}
                                                    >
                                                        <Icon name="IconChevronRight" size="sm" />
                                                    </Stack>
                                                </HStack>
                                            )}
                                        </HStack>
                                    )}

                                    {isCarousel ? (
                                        <Box {...styles.swiperWrapper}>
                                            <Swiper
                                                ref={swiperRef}
                                                modules={[Navigation]}
                                                navigation={{
                                                    prevEl: `.swiper-button-prev-color-selector`,
                                                    nextEl: `.swiper-button-next-color-selector`,
                                                }}
                                                spaceBetween={5}
                                                // Start scrolled so the currently selected colour is visible even
                                                // when it isn't within the first slides (clamped to the last page).
                                                initialSlide={Math.min(
                                                    currentColorIndex,
                                                    Math.max(0, selectableColorsLength - (slidesPerView && slidesPerView >= minSlidesToShow ? slidesPerView : minSlidesToShow)),
                                                )}
                                                onSwiper={(swiper) => scrollToCurrentColor(slidesPerView, swiper)}
                                                slidesPerView={slidesPerView && slidesPerView >= minSlidesToShow ? slidesPerView : minSlidesToShow}
                                            >
                                                {renderColors(colors, true)}
                                            </Swiper>
                                        </Box>
                                    ) : isMobile ? (
                                        // Mobile: vertical scrollable colour list (Figma). Desktop keeps the swatch row above.
                                        <VStack {...styles.colorsListMobile}>{renderColorsList(colors)}</VStack>
                                    ) : (
                                        <HStack {...styles.colorsRow}>{renderColors(colors)}</HStack>
                                    )}
                                </VStack>
                            )
                        })()}

                    {/* Sizes */}
                    <SizeSelector
                        product={product}
                        selectedSize={selectedSize}
                        isSizeListActive={isSizeListActive}
                        sizeErrorVisible={sizeErrorVisible}
                        setSizeErrorVisible={setSizeErrorVisible}
                        setSelectedSize={setSelectedSize}
                        focusFirstSize={!hasMultipleColors}
                    />

                    {/* Fixed divider between the scrollable list and the footer togglers (Figma).
                        Sits outside the scroll area so it's always visible, unlike the per-row dividers. */}
                    {isMobile && <Box {...styles.mobileListDivider} />}

                    {isMobile && (
                        <HStack {...styles.mobileTogglesRow}>
                            {/* Taglia toggler (left): chevron + label + selected size (right), mirroring the colour slot */}
                            <HStack as="button" {...styles.toggleSlot} onClick={() => setIsSizeListActive(!isSizeListActive)}>
                                <Icon name={isSizeListActive ? 'IconChevronUp' : 'IconChevronDown'} size="xs" />
                                <Text {...styles.toggleSlotLabel}>{sizeTabLabel}</Text>
                                {selectedSizeLabel && <Text {...styles.toggleSlotValue}>{selectedSizeLabel}</Text>}
                            </HStack>

                            {colors && colors?.length > 1 && (
                                <>
                                    <Box {...styles.toggleSeparator} />
                                    {/* Colore toggler (right): chevron + label + current colour dot */}
                                    <HStack as="button" {...styles.toggleSlot} onClick={() => setIsColorSwatchActive(!isColorSwatchActive)}>
                                        <Icon name={isColorSwatchActive ? 'IconChevronUp' : 'IconChevronDown'} size="xs" />
                                        <Text {...styles.toggleSlotLabel}>{colorTabLabel}</Text>
                                        <Box {...styles.colorTogglerSwatch} aria-hidden="true">
                                            <ColorCircle hexCodes={activeVariationGroup?.color?.colorHexCode ?? null} selected />
                                        </Box>
                                    </HStack>
                                </>
                            )}
                        </HStack>
                    )}

                    <Box {...styles.addToCartButtonWrapper}>
                        <HStack {...styles.addToCartRow} data-testid="add-to-cart-row">
                            {hasPriceRange && selectedVariantPrice && <Text {...styles.rangeRealPrice}>{selectedVariantPrice}</Text>}
                            <Button
                                data-testid="add-to-cart-button"
                                onClick={addProductToCart}
                                // Mobile (Figma): compact 42px button with button-xs text; desktop unchanged.
                                size={isMobile ? 'xs' : 'md'}
                            >
                                {formatMessage({id: 'buy.button', defaultMessage: 'Add to cart'})}
                            </Button>
                        </HStack>

                        {sizeErrorVisible && (
                            <Box {...styles.addToCartNoSizeWrapper}>
                                <Text as="span" {...styles.addToCartNoSize}>
                                    <FormattedMessage id="error.select.size" defaultMessage="Select the size to proceed" />
                                </Text>
                            </Box>
                        )}
                    </Box>
                </>
            )}
        </VStack>
    )
}

export {ProductTileOverlay}
